import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { classnames } from '../../../helpers/classnames';
import styles from './PDFViewer.css';
import { bem } from '../../../helpers/styles';
import { isPDFUrl } from '../../../helpers/url';
import useTranslate from '../../../hooks/useTranslate';
import Error from '../../atoms/Error/Error';
import arePropsEqual from '../../../helpers/arePropsEqual';

const PDFViewer = React.memo(({
  link,
  style,
  className,
  fixedMode,
}) => {
  const i18n = useTranslate();
  const ref = useRef(null);
  const [styleOverride, setStyle] = useState(null);
  const error = useMemo(() => !isPDFUrl(link), [link]);

  const finalStyle = useMemo(
    () =>
      fixedMode && styleOverride
        ? {
          ...style,
          position: 'fixed',
          ...styleOverride,
        }
        : style,
    [style, styleOverride],
  );

  // En fixedMode on retaille le div principal pour "rentrer" dans le div parent
  // eslint-disable-next-line max-len
  // https://stackoverflow.com/questions/5873565/set-width-of-a-position-fixed-div-relative-to-parent-div
  useEffect(() => {
    if (!fixedMode || !ref.current?.parentNode) {
      return;
    }

    const updateStyle = () => {
      if (!ref.current?.parentNode) return;
      const parentRect = ref.current.parentNode.getBoundingClientRect();
      // We compute maxHeight without space on top and 72px on the bottom (height of
      // the "submit" zone)
      const maxHeight = window.innerHeight - parentRect.top - 72;

      setStyle({
        ...style,
        width: parentRect.width - 16,
        height: Math.min(parentRect.height - 16, maxHeight),
      });
    };
    updateStyle();

    // ResizeObserver pour observer les changements de taille du parent
    const resizeObserver = new ResizeObserver(updateStyle);

    resizeObserver.observe(ref.current?.parentNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fixedMode, style]);

  if (error) return <Error>{i18n('PDFViewer.error.description')}</Error>;

  return (
    <div style={finalStyle} className={classnames([styles.PDFViewer, className])} ref={ref}>
      <iframe
        src={link}
        className={bem(styles, 'PDFViewer__embed', { fixedMode })}
        type="application/pdf"
        tabIndex={-1}
      />
    </div>
  );
}, arePropsEqual);

PDFViewer.displayName = 'PDFViewer';

PDFViewer.propTypes = {
  className: PropTypes.string,
  link: PropTypes.string.isRequired,
  style: PropTypes.object,
  fixedMode: PropTypes.bool,
};

export default PDFViewer;
